import PropTypes from "prop-types";
import React from "react";
import { HiSparkles } from "react-icons/hi2";
import { useLocation, useNavigate } from "react-router-dom";
import { cn } from "../../../../helpers/utils/css-utils";
import { isCopilotConfigured } from "../../../copilot_native/api/copilot-api";
import { copilotPathFor, isCopilotPath } from "../../../copilot_native/model/copilot-paths";

/**
 * Opens the Agent Builder Copilot page from the top of the left rail: inside an
 * application it builds that application, elsewhere it works for the account.
 * Renders nothing when the copilot is not configured.
 */
export default function CopilotLaunch({ appId, compact = false }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  if (!isCopilotConfigured()) return null;

  const active = isCopilotPath(pathname);
  const target = copilotPathFor(pathname.startsWith("/app/") ? appId : null);

  return (
    <div className={compact ? "px-1" : "px-0.5 pb-1 pt-2"}>
      <button
        type="button"
        onClick={() => navigate(target)}
        aria-current={active ? "page" : undefined}
        title={appId ? "Build this agent with Console" : "Create and find agents with Console"}
        className={cn(
          `group relative flex w-full items-center gap-2.5 overflow-hidden rounded-xl px-2.5 py-2 text-left
           outline-none transition-all duration-150
           focus-visible:ring-2 focus-visible:ring-accent/40`,
          compact && "pr-6",
          active
            ? "bg-[#111827] text-fixed-white shadow-md ring-1 ring-brand-lime/60 dark:bg-raised"
            : "bg-[#111827] text-fixed-white shadow-sm hover:-translate-y-px hover:shadow-md dark:bg-raised dark:ring-1 dark:ring-line"
        )}
      >
        {/* soft lime glow on hover / when active */}
        <span
          aria-hidden="true"
          className={cn(
            "pointer-events-none absolute -right-6 -top-6 h-16 w-16 rounded-full bg-brand-lime/30 blur-2xl transition-opacity duration-300",
            active ? "opacity-100" : "opacity-0 group-hover:opacity-100"
          )}
        />
        <span className="relative flex h-7 w-7 flex-none items-center justify-center rounded-lg bg-fixed-white/10 text-brand-lime">
          <HiSparkles className="h-4 w-4" />
        </span>
        <span className="relative min-w-0 flex-1 leading-tight">
          <span className="block text-[13.5px] font-semibold">Console</span>
          <span className="block truncate text-[11px] text-fixed-white/60">
            {appId ? "Build with AI" : "Create an agent with AI"}
          </span>
        </span>
        {active && <span className="relative h-1.5 w-1.5 flex-none rounded-full bg-brand-lime" />}
      </button>
    </div>
  );
}

CopilotLaunch.propTypes = {
  appId: PropTypes.string,
  compact: PropTypes.bool,
};
